/** RUN-003's activation verdict, read against a live entity. Pure and read-only. */

import type { AbilityDecl, ActiveEffect } from "@nebulize/sdk";
import type { StateEntity } from "@/lib/runtime";

export type RefusalReason = "blocked_by_tag" | "missing_tag" | "on_cooldown" | "cannot_afford";

export interface ActivationVerdict {
  allowed: boolean;
  reason: RefusalReason | null;
  blockingTag: string | null;
}

/** RUN-003's cooldown tag: `activity.<id>.cooling.<ability>`. */
export function cooldownTag(activityId: string, abilityName: string): string {
  return `activity.${activityId}.cooling.${abilityName}`;
}

/** A tag matches a query for any of its dotted prefixes, exactly as `TagSet.has` does. */
function hasTag(tags: readonly string[], want: string): boolean {
  return tags.some((one) => one === want || one.startsWith(`${want}.`));
}

/** The activator's base value of the cost attribute, or null if it holds none. */
function baseValueOf(entity: StateEntity, attribute: string): number | null {
  return entity.attributes[attribute]?.value ?? null;
}

/** `has_not`, then `has`, then cooldown, then cost, first failure wins. RUN-003's own order. */
export function activationVerdict(
  decl: AbilityDecl,
  activityId: string,
  abilityName: string,
  entity: StateEntity,
): ActivationVerdict {
  const tags = entity.tags.tags;

  for (const tag of decl.has_not ?? []) {
    if (hasTag(tags, tag)) return { allowed: false, reason: "blocked_by_tag", blockingTag: tag };
  }
  for (const tag of decl.has ?? []) {
    if (!hasTag(tags, tag)) return { allowed: false, reason: "missing_tag", blockingTag: tag };
  }
  if (hasTag(tags, cooldownTag(activityId, abilityName))) {
    return { allowed: false, reason: "on_cooldown", blockingTag: null };
  }
  if (decl.cost != null) {
    const held = baseValueOf(entity, decl.cost.attribute);
    if (held === null || held < decl.cost.amount) {
      return { allowed: false, reason: "cannot_afford", blockingTag: null };
    }
  }
  return { allowed: true, reason: null, blockingTag: null };
}

/** The cooldown's own running instance, so a chip can show what is left of it. */
export function cooldownEffect(entity: StateEntity, abilityName: string): ActiveEffect | null {
  return Object.values(entity.effects ?? {}).find((one) => one.name === abilityName) ?? null;
}
